import type {
  AccessLegitimacyEnvelope,
  AdminSettingsInput,
  AdminSettingsOutput,
  ConnectedService,
  DashboardFeatureFlags,
  DashboardSettings,
  EffectiveSettingsOutput,
  McpGrantListItem,
  McpGrantListResponse,
  McpOAuthClientInfo,
  McpOAuthDecisionRequest,
  McpOAuthDecisionResponse,
  PartialAdminSettingsInput,
  PartialTenantSettingsInput,
  RoleRef,
  TenantSettingsInput,
  TenantSettingsOutput,
  WebPermission,
  WidgetDataEnvelope,
  WidgetOverride,
  WidgetPositionOverride,
  WidgetResponseStatus,
} from '@dawn/feature-config';
import {
  ACCESS_LEGITIMACY_ANALYSED_ONLY_HEADER,
  ACCESS_LEGITIMACY_CHART_AS_OF_HEADER,
  ACCESS_LEGITIMACY_SCOPE_CONTRACT_HEADER,
  isConnectedService,
  RBAC_ERROR_CODES,
} from '@dawn/feature-config';
import axios, { type AxiosError } from 'axios';

import { useSessionStore } from '@/stores/session';
import type { TenantCompany } from '@/stores/tenant';
import { useTenantLifecycleStore } from '@/stores/tenant-lifecycle';

import type { AccessLegitimacyRequestParams } from './access-legitimacy-params';

export type {
  DashboardSettings,
  WidgetDataEnvelope,
  WidgetOverride,
  WidgetPositionOverride,
  WidgetResponseStatus,
};

// Same-origin deployment: Vite proxies /api in dev, nginx routes /api to the
// API service in prod. Hardcoded because the frontend must know this path to
// bootstrap the /config fetch — it can't be server-driven. Exported so the MCP
// connection instructions can build the public endpoint URL from the same base.
export const API_URL = '/api/v1/dashboard';

export const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor to handle session error codes
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const isAuthEndpoint = error.config?.url?.startsWith('/auth/');
    const errorCode = (error.response?.data as Record<string, unknown>)?.errorCode;

    if (error.response?.status === 401 && !isAuthEndpoint) {
      const { setExpired } = useSessionStore.getState();
      if (errorCode === 'SESSION_EXPIRED') {
        setExpired('inactivity');
      } else if (errorCode === 'SESSION_REVOKED') {
        setExpired('revoked');
      } else {
        setExpired('invalid');
      }
    }

    // Tenant off-boarding block (TDEV-571 A4): a distinct 403, not a session
    // expiry (re-login can't clear it) — flip the dedicated flag so the app
    // renders the "account deactivated" page.
    if (error.response?.status === 403 && errorCode === 'TENANT_DEACTIVATED') {
      useTenantLifecycleStore.getState().setDeactivated();
    }

    // A role removed mid-session: re-read /auth/me so the page gates catch up (TDEV-1213).
    if (error.response?.status === 403 && errorCode === RBAC_ERROR_CODES.permissionDenied) {
      void refreshSessionUser();
    }
    return Promise.reject(error);
  }
);

// API response types
export interface User {
  id: string;
  email: string;
  name: string;
  tenantId: string;
  /** Held permissions among WEB_PERMISSIONS (TDEV-1213); the server checks everything else itself. */
  permissions: WebPermission[];
}

interface LoginResponse {
  user: User;
  sessionCreatedAt: number;
  maxAllowedTime: number;
}

interface MeResponse {
  user: User;
  sessionCreatedAt: number;
  maxAllowedTime: number;
}

export interface VerificationRequiredResponse {
  status: 'verification_required';
  verificationId: string;
  maskedEmail: string;
}

export interface ResendCodeResponse {
  success: boolean;
  message: string;
}

export interface AdminListItem {
  id: string;
  email: string;
  authMethod: string;
  ssoProvider?: string | null;
  /** Roles the admin holds (TDEV-1213); the Roles column. */
  roles: RoleRef[];
  /** Suspension timestamp from the roster: null = active, non-null = suspended (TDEV-140). */
  suspendedAt?: string | null;
  lastLogin?: string | null;
  createdAt: string;
}

/**
 * A not-yet-accepted invitation. Pending/expired status is derived on the FE
 * from `expiresAt` — the backend returns both so the roster can render and
 * count them differently (TDEV-141).
 */
export interface InvitationListItem {
  id: string;
  email: string;
  expiresAt: string;
  createdAt: string;
  /** Roles the invitee gets on acceptance; [] for a role-less legacy invitation. */
  roles: RoleRef[];
}

export interface AdminRoster {
  admins: AdminListItem[];
  invitations: InvitationListItem[];
  /** The tenant's roles, Super Admin → Admin → Analyst — the invite picker's options (TDEV-1213). */
  availableRoles: RoleRef[];
}

type AdminListResponse = AdminRoster;

// API functions
export const authApi = {
  // Local login flow
  login: async (email: string, password: string): Promise<VerificationRequiredResponse> => {
    const response = await api.post<VerificationRequiredResponse>('/auth/login', {
      email,
      password,
    });
    return response.data;
  },
  verifyCode: async (verificationId: string, code: string): Promise<LoginResponse> => {
    const response = await api.post<LoginResponse>('/auth/verify-code', {
      verificationId,
      code,
    });
    return response.data;
  },
  resendCode: async (verificationId: string): Promise<ResendCodeResponse> => {
    const response = await api.post<ResendCodeResponse>('/auth/resend-code', {
      verificationId,
    });
    return response.data;
  },
  // Google OAuth login
  googleLogin: async (accessToken: string): Promise<LoginResponse> => {
    // Tenant ID is derived from subdomain on the backend via TenantResolutionMiddleware
    // Invitation is looked up by (tenant_id, email from Google OAuth) - no token needed
    const response = await api.post<LoginResponse>('/auth/google', {
      accessToken,
    });
    return response.data;
  },
  // Identity Platform login
  identityPlatformLogin: async (idToken: string): Promise<LoginResponse> => {
    const response = await api.post<LoginResponse>('/auth/identity-platform', {
      idToken,
    });
    return response.data;
  },
  logout: async () => {
    const response = await api.post('/auth/logout');
    return response.data;
  },
  me: async (signal?: AbortSignal): Promise<MeResponse | null> => {
    const response = await api.get<MeResponse | Record<string, never>>('/auth/me', { signal });
    return 'user' in response.data ? (response.data as MeResponse) : null;
  },
};

let refreshing: { user: User | null; done: Promise<void> } | null = null;

/**
 * Re-reads /auth/me so a role changed mid-session reaches the permission gates
 * (TDEV-1213). Concurrent callers within one session share one request. Writes
 * `user` only: `setActive` would also reset the session status and banners.
 */
export function refreshSessionUser(): Promise<void> {
  // Every login, logout or refresh stores a new user object, so its identity marks the session.
  const user = useSessionStore.getState().user;
  if (refreshing?.user === user) return refreshing.done;
  const current: { user: User | null; done: Promise<void> } = { user, done: Promise.resolve() };
  current.done = authApi
    .me()
    .then((data) => {
      // A logout or any login while this was in flight wins over the stale answer.
      if (data && useSessionStore.getState().user === user) {
        useSessionStore.setState({ user: data.user });
      }
    })
    .catch(() => undefined) // a 401 is the interceptor's job; the gates keep their last state
    .finally(() => {
      if (refreshing === current) refreshing = null;
    });
  refreshing = current;
  return current.done;
}

export const sessionApi = {
  ping: async () => {
    await api.post('/session/ping');
  },
};

export const usersApi = {
  getAll: async () => {
    const response = await api.get('/users');
    return response.data;
  },
  getById: async (id: string) => {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },
};

export const adminsApi = {
  getRoster: async (signal?: AbortSignal): Promise<AdminRoster> => {
    const response = await api.get<AdminListResponse>('/admins', { signal });
    const { admins, invitations, availableRoles } = response.data;
    return { admins, invitations, availableRoles };
  },

  // Back-compat thin accessor for the legacy Users page (admins only).
  getAll: async (signal?: AbortSignal): Promise<AdminListItem[]> => {
    const { admins } = await adminsApi.getRoster(signal);
    return admins;
  },

  // Real endpoint (TDEV-141) — sends the invitation and pre-provisions the user.
  // The caller supplies the email and the roles (required, TDEV-1213); tenant is
  // derived server-side.
  invite: async (email: string, roleIds: string[]): Promise<{ success: boolean }> => {
    const response = await api.post<{ success: boolean }>('/admins/invite', { email, roleIds });
    return response.data;
  },

  // --- Live lifecycle mutations (TDEV-140, behind `users.manage`) ------------
  // Each hits a real endpoint; callers refetch the roster on success (and on a
  // 404/409 "already changed") so the table reflects server truth. The returned
  // shapes are informational — the refetch, not the response, drives the table.

  // Revives an expired/pending invite to a fresh expiry window and re-sends it.
  resendInvitation: async (invitationId: string): Promise<{ id: string; expiresAt: string }> => {
    const response = await api.post<{ id: string; expiresAt: string }>(
      `/admins/invitations/${invitationId}/resend`
    );
    return response.data;
  },

  // Cancels a not-yet-accepted invitation, freeing its slot.
  revokeInvitation: async (invitationId: string): Promise<{ id: string }> => {
    const response = await api.post<{ id: string }>(`/admins/invitations/${invitationId}/revoke`);
    return response.data;
  },

  // Ends a colleague's access while keeping their seat (still counts to the cap).
  suspendAdmin: async (adminId: string): Promise<{ id: string; suspendedAt: string }> => {
    const response = await api.post<{ id: string; suspendedAt: string }>(
      `/admins/${adminId}/suspend`
    );
    return response.data;
  },

  // Restores a suspended admin's access.
  activateAdmin: async (adminId: string): Promise<{ id: string }> => {
    const response = await api.post<{ id: string }>(`/admins/${adminId}/activate`);
    return response.data;
  },

  // Replaces an admin's roles (TDEV-1213); effective on their next request.
  updateAdminRoles: async (
    adminId: string,
    roleIds: string[]
  ): Promise<{ id: string; roles: RoleRef[] }> => {
    const response = await api.put<{ id: string; roles: RoleRef[] }>(`/admins/${adminId}/roles`, {
      roleIds,
    });
    return response.data;
  },

  // Replaces a pending or expired invitation's roles (TDEV-1213); no email is sent.
  updateInvitationRoles: async (
    invitationId: string,
    roleIds: string[]
  ): Promise<{ id: string; roles: RoleRef[] }> => {
    const response = await api.put<{ id: string; roles: RoleRef[] }>(
      `/admins/invitations/${invitationId}/roles`,
      { roleIds }
    );
    return response.data;
  },
};

export const companyApi = {
  getInfo: async (signal?: AbortSignal): Promise<TenantCompany> => {
    const response = await api.get<TenantCompany>('/company', { signal });
    return response.data;
  },
};

/**
 * Widened settings shape (TDEV-266 Phase 5.1). Previously `TenantSettings` was a
 * thin tenant-only interface; now it aliases `EffectiveSettings` from
 * `@dawn/feature-config`, which is `TenantSettings ∪ admin-only keys`
 * (sidebar / globalFilters / widgetFilters). The GET /settings
 * server already returns this shape post-merge; the FE now types it correctly.
 *
 * Public name kept as `TenantSettings` for backwards compatibility with
 * existing imports; once Phase 6 migrates consumers to `EffectiveSettings`,
 * the alias can be removed.
 */
export type TenantSettings = EffectiveSettingsOutput;
export type EffectiveSettings = EffectiveSettingsOutput;

export const settingsApi = {
  getSettings: (signal?: AbortSignal) =>
    api.get<EffectiveSettings>('/settings', { signal }).then((r) => r.data),

  // Admin settings — PATCH/PUT/DELETE the per-admin row.
  //
  // REM-FIX (design.md §3.2): these endpoints return the raw admin row
  // (AdminSettingsOutput), NOT the merged EffectiveSettings blob. Callers
  // must overlay the row response onto the effective cache via deepMerge
  // (Option B FE-side merge); pinning the row would clobber tenant-derived
  // keys (e.g. `dashboard`) and admin-only keys absent from the response.
  // DELETE returns 204 No Content (Promise<void>) — no body to merge.
  patchAdminSettings: (partial: PartialAdminSettingsInput): Promise<AdminSettingsOutput> =>
    api.patch<AdminSettingsOutput>('/settings/admin', partial).then((r) => r.data),
  replaceAdminSettings: (full: AdminSettingsInput): Promise<AdminSettingsOutput> =>
    api.put<AdminSettingsOutput>('/settings/admin', full).then((r) => r.data),
  deleteAdminSettings: (): Promise<void> =>
    api.delete<void>('/settings/admin').then(() => undefined),

  // Tenant settings — PATCH/PUT the tenant row (admin-only, but exposed so the
  // FE can drive a future tenant-config admin UI; not consumed in V1).
  // Returns the raw tenant row (TenantSettingsOutput) per design.md §3.2.
  patchTenantSettings: (partial: PartialTenantSettingsInput): Promise<TenantSettingsOutput> =>
    api.patch<TenantSettingsOutput>('/settings/tenant', partial).then((r) => r.data),
  replaceTenantSettings: (full: TenantSettingsInput): Promise<TenantSettingsOutput> =>
    api.put<TenantSettingsOutput>('/settings/tenant', full).then((r) => r.data),
};

export interface SessionPolicyResponse {
  inactivityTimeoutSeconds: number;
  inactivityWarningOffsetSeconds: number;
  maxDurationSeconds: number;
  maxDurationWarningOffsetSeconds: number;
  autoRedirectSeconds: number;
  pingIntervalSeconds: number;
}

export interface FirebaseClientConfigResponse {
  apiKey: string;
  authDomain: string;
  authEmulatorHost?: string;
}

export interface ConfigResponse {
  featureFlags: DashboardFeatureFlags;
  firebase: FirebaseClientConfigResponse;
  session?: SessionPolicyResponse;
  /** Admin + pending-invitation cap per tenant. Present only in the authenticated response. */
  maxAdminsPerTenant?: number;
}

export const configApi = {
  getConfig: (signal?: AbortSignal) =>
    api.get<ConfigResponse>('/config', { signal }).then((r) => r.data),
};

/**
 * MCP OAuth (TDEV-1199): the consent page's two calls (both ride the session
 * cookie) and the connected-apps list (`users.manage`). Types are the api↔web
 * contract from @dawn/feature-config.
 */
/** Which connected apps: every Admin's (`users.manage`) or the caller's own (`data.view`, TDEV-1234). */
export type McpGrantScope = 'tenant' | 'own';

const MCP_GRANTS_PATH: Record<McpGrantScope, string> = {
  tenant: '/mcp/oauth/grants',
  own: '/mcp/oauth/grants/own',
};

export const mcpOAuthApi = {
  /** The authorization request's own query parameters, re-validated server-side. */
  client: async (
    params: Record<string, string>,
    signal?: AbortSignal
  ): Promise<McpOAuthClientInfo> => {
    const response = await api.get<McpOAuthClientInfo>('/mcp/oauth/client', { params, signal });
    return response.data;
  },
  decide: async (body: McpOAuthDecisionRequest): Promise<McpOAuthDecisionResponse> => {
    const response = await api.post<McpOAuthDecisionResponse>('/mcp/oauth/decision', body);
    return response.data;
  },
  listGrants: async (scope: McpGrantScope, signal?: AbortSignal): Promise<McpGrantListItem[]> => {
    const response = await api.get<McpGrantListResponse>(MCP_GRANTS_PATH[scope], { signal });
    return response.data.grants;
  },
  revokeGrant: async (scope: McpGrantScope, id: string): Promise<void> => {
    await api.post(`${MCP_GRANTS_PATH[scope]}/${id}/revoke`);
  },
};

export const dashboardApi = {
  /** The tenant's connected services; a malformed answer fails, so the filter keeps every service. */
  getConnectedServices: async (signal?: AbortSignal): Promise<ConnectedService[]> => {
    const envelope = await dashboardApi.getWidgetData('connected-services', {}, signal);
    const services =
      envelope.status === 'data'
        ? (envelope.data as { services?: unknown } | undefined)?.services
        : undefined;
    if (!Array.isArray(services))
      throw new Error('connected-services answered without a services list');
    return services.filter(isConnectedService);
  },

  getWidgetData: async (
    endpoint: string,
    filters: Record<string, string>,
    signal?: AbortSignal
  ): Promise<WidgetDataEnvelope> => {
    const response = await api.get<WidgetDataEnvelope>(endpoint, {
      params: filters,
      signal,
    });
    return response.data;
  },

  // The events grid is the first POST data client and the only dashboard read
  // that does NOT return `WidgetDataEnvelope` — it returns the raw envelope by
  // deliberate backend choice (design v6 §3.3, Open Question 7). The filter
  // body rides in the POST payload, not query params.
  getAccessLegitimacy: async (
    params: AccessLegitimacyRequestParams,
    signal?: AbortSignal
  ): Promise<AccessLegitimacyEnvelope> => {
    // A header, not a body field: an API pod from before it rejects unknown fields.
    const { scopeContract, asOf, analysedOnly, ...body } = params;
    const headers = {
      ...(scopeContract !== undefined
        ? { [ACCESS_LEGITIMACY_SCOPE_CONTRACT_HEADER]: String(scopeContract) }
        : {}),
      ...(asOf !== undefined ? { [ACCESS_LEGITIMACY_CHART_AS_OF_HEADER]: asOf } : {}),
      ...(analysedOnly ? { [ACCESS_LEGITIMACY_ANALYSED_ONLY_HEADER]: '1' } : {}),
    };
    const response = await api.post<AccessLegitimacyEnvelope>('access-legitimacy', body, {
      signal,
      ...(Object.keys(headers).length > 0 ? { headers } : {}),
    });
    return response.data;
  },
};
